import type {
  AdminUser,
  AdminUserProfileUpdate,
} from '../../api/extensions/useAdminOps';

// US state codes — matches the user-facing profile form's option set so the
// admin-edit inputs look identical whether the admin uses the inline page
// editor or the row-level modal on the Manage Users list.
export const US_STATES = [
  'AL','AK','AZ','AR','CA','CO','CT','DE','FL','GA',
  'HI','ID','IL','IN','IA','KS','KY','LA','ME','MD',
  'MA','MI','MN','MS','MO','MT','NE','NV','NH','NJ',
  'NM','NY','NC','ND','OH','OK','OR','PA','RI','SC',
  'SD','TN','TX','UT','VT','VA','WA','WV','WI','WY',
] as const;

export interface AdminProfileFormData {
  first_name: string;
  last_name: string;
  display_first_name: string;
  display_last_name: string;
  primary_email: string;
  phone: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  zip: string;
  date_of_birth: string;
  gender: string;
}

export type AdminProfileFieldKey = keyof AdminProfileFormData;

export function emptyAdminProfileForm(): AdminProfileFormData {
  return {
    first_name: '',
    last_name: '',
    display_first_name: '',
    display_last_name: '',
    primary_email: '',
    phone: '',
    address1: '',
    address2: '',
    city: '',
    state: '',
    zip: '',
    date_of_birth: '',
    gender: '',
  };
}

export function userToAdminProfileForm(user: AdminUser): AdminProfileFormData {
  return {
    first_name: user.first_name ?? '',
    last_name: user.last_name ?? '',
    display_first_name: user.display_first_name ?? '',
    display_last_name: user.display_last_name ?? '',
    primary_email: user.primary_email ?? '',
    phone: user.phone ?? '',
    address1: user.address1 ?? '',
    address2: user.address2 ?? '',
    city: user.city ?? '',
    state: user.state ?? '',
    zip: user.zip ?? '',
    date_of_birth: user.date_of_birth
      ? String(user.date_of_birth).substring(0, 10)
      : '',
    gender: user.gender ?? '',
  };
}

export function formatPhoneNumber(value: string): string {
  const digits = value.replace(/\D/g, '');
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6, 10)}`;
}

/**
 * Normalise a single field write. Phone gets formatted as (XXX) XXX-XXXX and
 * caps at 10 digits; zip strips everything except digits + hyphen and caps at
 * 10 characters. Other fields pass through. Returns `null` to indicate the
 * write should be dropped entirely (e.g. typing a 12th phone digit).
 */
export function normaliseAdminProfileField(
  field: AdminProfileFieldKey,
  value: string,
): string | null {
  if (field === 'phone') {
    const digits = value.replace(/\D/g, '');
    if (digits.length > 10) return null;
    return formatPhoneNumber(value);
  }
  if (field === 'zip') {
    return value.replace(/[^\d-]/g, '').slice(0, 10);
  }
  return value;
}

/**
 * Build the PATCH payload sent to the admin user update endpoint. Only the
 * fields the admin actually changed are included so that:
 *   (1) untouched fields can never accidentally get overwritten with empty
 *       strings, and
 *   (2) blank fields can be CLEARED — explicitly setting a field to empty
 *       string is serialised as `null`, the canonical "no value" for these
 *       nullable columns.
 */
export function buildAdminProfileDiff(
  original: AdminProfileFormData,
  draft: AdminProfileFormData,
): AdminUserProfileUpdate {
  const diff: AdminUserProfileUpdate = {};
  (Object.keys(draft) as AdminProfileFieldKey[]).forEach((key) => {
    if (draft[key] !== original[key]) {
      diff[key] = draft[key] === '' ? null : draft[key];
    }
  });
  return diff;
}

export function formatDateOfBirthForDisplay(value: string): string {
  if (!value) return '';
  // DOB is stored as a plain date (YYYY-MM-DD). Anchor parsing to local
  // midnight so timezone offsets can't shift the displayed day by one.
  try {
    const d = new Date(`${value.substring(0, 10)}T00:00:00`);
    if (Number.isNaN(d.getTime())) return value;
    return d.toLocaleDateString();
  } catch {
    return value;
  }
}
